/**
  Author: Lokesh Arora - lokesh.arora@enabledata.com
  Purpose: Users add/edit form fields
  Date: April 25, 2020
**/

import React, { useEffect, useRef } from 'react';
import {
  Grid,
  TextField,
  makeStyles,
  Button,
  FormHelperText,
  Select,
  InputLabel,
  FormControl,
  MenuItem,
  Tooltip,
  Typography,
  Box,
  Divider,
  IconButton,
} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { Formik, FieldArray, getIn } from 'formik';
import * as Yup from 'yup';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { PopoverBtn } from '../../../../components';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { globalDateEntity, globalPageTypeEntity, currentUserEntity, DatepickerStartDate, ROLE_POLICY_MANAGER, ROLE_POLICY_OPERATOR } from '../../../../constants/applicationConstants';
// import { capitalize } from '../../../../utils/common';
import { stringConstants } from '../../../../constants/stringConstants';
import styles from './userFormFields.module.scss';
import moment from 'moment';
import AddBoxIcon from '@material-ui/icons/AddBox';
import DeleteIcon from '@material-ui/icons/Delete';
import MuiPhoneNumber from 'material-ui-phone-number';
import { RouteLeavingGuard } from '../../../../components';
import EnhancedAutoComplete from '../../../../components/enhancedAutoComplete/EnhancedAutoComplete';

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: '100%',
  },
  mainPadding: {
    padding: '0 20px',
  },
  autoComp: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
  popOver: {
    paddingTop: '30px !important',
  },
  selectAdornment: {
    marginRight: theme.spacing(3),
  },
  txtlowercase: {
    "& .MuiSelect-select": {
      textTransform: 'lowercase !important'
    }
  },
  borderedGrid: {
    borderRadius: '3px',
    border: '1px solid #ccc'
  }
}));

const UserFormFields = (props) => {
  const classes = useStyles();
  const formRef = useRef();
  const {
    history,
    pageType,
    formValues,
    cancelClick,
    onDone,
  } = props;
  const loginAction = useStoreActions((actions) => actions.login);
  const commonAction = useStoreActions((actions) => actions.common);
  const userAction = useStoreActions((actions) => actions.userList);
  const userState = useStoreState((state) => state.userList);
  const customizeAction = useStoreActions((actions) => actions.customize);

  //Organization ID to be used for submission of data
  const orgId = useStoreState((state) => state.organization.organizationId);
  const userInfo = useStoreState((state) => state.login.userInfo);
  const orgAction = useStoreActions((actions) => actions.organization);
  const [isDirtyForm, setIsDirtyForm] = React.useState(false); 

  const configData = useStoreState((state) => state.customize.configData);

  const [policyTypesDefaultValues, setpolicyTypesDefaultValues] = React.useState([]);

  const [policyListField, setPolicyListField] = React.useState(false);
  const [policyListFieldKey, setPolicyListFieldKey] = React.useState(new Date().getTime());
  const [productRoles, setProductRoles] = React.useState({});
  
  const settingsState = useStoreState((state) => state.settings);
  const [settingsAction] = useStoreActions((actions) => [actions.settings, actions.login]);
  const [policyTypesData, setPolicyTypesData] = React.useState();

  const [domainsList, setDomainsList] = React.useState([]);
  const [isTenantAdmin, setIsTenantAdmin] = React.useState(true);
  const [initialTenantAdmin, setInitialTenantAdmin] = React.useState(false);

  const [fieldData, setFieldData] = React.useState({
    username: '',
    firstname: '',
    lastname: '',
    current_status: 'ACTIVE',
    email: '',
    phone: '',
    role: '',
    orgs: orgId,
    showEmail:userInfo && userInfo.roles && userInfo.roles.title && userInfo.roles.title.toLowerCase() === currentUserEntity.platformAdminTitle.toLowerCase(),
    inactivedate: null,
    user_policies: [],
    email_domain: '',
    email_username:'',
    userRoles: [
      {
        id: Math.random(), //NOSONAR
        productCode: "",
        roleId: ""
      }
    ],
    root_user: 0
  });


  useEffect(() => {
    // roleAction.getRoleList();
    customizeAction.getConfigData();
    userAction.getProductRoles(orgId);
  }, []);

  useEffect(() => {
    setProductRoles(userState.productRoles);
  }, [userState.productRoles])

  useEffect(() => {
    if (configData?.allowed_domains) {
      setDomainsList(configData.allowed_domains);
      let defaultDomain = configData.allowed_domains.filter((item) => item.isDefault==true);

      if (defaultDomain.length>0 && defaultDomain[0] && defaultDomain[0].domain) {
        const updatedFieldData = Object.assign({}, fieldData);
        updatedFieldData.email_domain = defaultDomain[0].domain;
        // setting the default domain
        setFieldData(updatedFieldData);
      }
    }
  }, [configData]);

  //Validation Schema for formik
  const schema = Yup.object().shape({
    username: Yup.string()
      .required(stringConstants.requiredValidationMsg)
      .min(4, stringConstants.userCharLimitValidMsg)
      .max(70, stringConstants.userMaxCharLimitValidMsg)
      .matches(/^[A-Za-z0-9]+$/, `User name ${stringConstants.specialCharValidationMsg}`),
    firstname: Yup.string()
      .required(stringConstants.requiredValidationMsg)
      .max(30, `First Name ${stringConstants.thirtyCharLimitValidMsg}`),
    lastname: Yup.string()
      .required(stringConstants.requiredValidationMsg)
      .max(30, `Last Name ${stringConstants.thirtyCharLimitValidMsg}`),
    phone: Yup.string()
      .nullable()
      .matches(
        /^\+[1]((\s)[(]?\d{3}[)]?[(\s)?.-]\d{3}[\s.-]\d{4})?$/g,// NOSONAR
        stringConstants.phoneValidationMsg,
      ),
    showEmail: Yup.boolean(),
    email_username: Yup.string()
      .max(100, `Email ${stringConstants.hundredCharLimitValidMsg}`)
      .when("showEmail", {
        is: false,
        then: Yup.string().required(stringConstants.requiredValidationMsg)
        .matches(/^[A-Za-z0-9._-]+$/, `Email ${stringConstants.specialCharValidationMsg}`)
      }),
      email: Yup.string()
      .when("showEmail", {
        is: true,
        then: Yup.string().email(stringConstants.validEmailMsg).required(stringConstants.requiredValidationMsg)
        .matches(/^[A-Za-z0-9._@-]+$/, `Email ${stringConstants.specialCharValidationMsg}`)
        .max(100, `Email ${stringConstants.hundredCharLimitValidMsg}`)
      }),
    role: Yup.string().nullable(),
    inactivedate: Yup.date().nullable().typeError(stringConstants.invalidDate).min(new Date(DatepickerStartDate), stringConstants.minDate),
    user_policies: Yup.array().nullable().when('role', {
      is: (role) => {
        if(isTenantAdmin) {
          return false
        }
        else if([ROLE_POLICY_MANAGER, ROLE_POLICY_OPERATOR].indexOf(role) !== -1){
          return true
        } else {
          return false
        }
      },
      then: Yup.array().min(1, stringConstants.requiredValidationMsg).required(stringConstants.requiredValidationMsg),
      otherwise: Yup.array().notRequired()
    }),
    isTenantAdmin: Yup.string(),

    userRoles: Yup.array().of(
      Yup.object().shape({
        productCode: isTenantAdmin ? Yup.string() : Yup.string().required(stringConstants.requiredValidationMsg),
        roleId: isTenantAdmin ? Yup.string() : Yup.string().required(stringConstants.requiredValidationMsg)
      })
    ),

  });
  const onFormSubmit = async (values, { resetForm, setErrors }) => {   
    try {
      values.SK = values.PK;

      const { PK, SK, inactivedate, orgs, user_policies = [], ...temp } = values;

      if (orgs === '') {
        setErrors({ 'orgs': stringConstants.requiredValidationMsg });
        return false;
      }

      let permissions = null;

      if (!isTenantAdmin) {
        let permissionsObj = {}
        values.userRoles.forEach((item) => {
          permissionsObj[item.productCode] = item.roleId
        });
        permissions = permissionsObj
      }
      else {
        // temp.role = currentUserEntity.tenantAdminOrgsRole;
        temp.role = userInfo.roles.PK;
      }
      
      if (permissions !== null) {
        temp.role = null;
        values.role = null;

        temp.roles = null;
        values.roles = null;
      }

      if (userInfo && userInfo.roles && userInfo.roles.title && (userInfo.roles.title.toLowerCase() !== currentUserEntity.platformAdminTitle.toLowerCase())) {
        temp.email = temp.email_username.trim().toLowerCase() + '@' + temp.email_domain;
      }

      delete temp.roles;

      delete temp.userRoles;
      
      if (PK && SK) {
        userAction
          .updateUser({
            ...temp,
            user_policies: (temp.role == currentUserEntity.tenantAdminOrgsRole) ? [] : user_policies,
            orgs_id: orgs,
            PK,
            SK,
            inactivedate: inactivedate ? moment(inactivedate).format(globalDateEntity.dateTimeFormatPost) : null,
            permissions:permissions,
          })
          .then(function (response) {
            commonAction.setSubmitSnackbar({
              status: true,
              messages: stringConstants.updateMsg,
            });
            setIsDirtyForm(false);
            resetForm();
            onDone();
          })
          .catch(function (error) {
            if(error.response && error.response.status === 422 && error.response.data.error_type === "validation_error") {
              let outputErrors = {};
              const errorlist = error.response.data.error;
              for (let item in errorlist) {
                if(userInfo && 
                  userInfo.roles && 
                  userInfo.roles.title && 
                  (userInfo.roles.title.toLowerCase() !== currentUserEntity.platformAdminTitle.toLowerCase())
                ) {
                  if(errorlist && errorlist[item] && Object.keys(errorlist[item]) && Object.keys(errorlist[item])[0] === 'email') {
                    outputErrors['email_username'] =
                      errorlist[item][Object.keys(errorlist[item])];
                  } else {
                    outputErrors[Object.keys(errorlist[item])] =
                      errorlist[item][Object.keys(errorlist[item])];
                  }                  
                } else {
                  outputErrors[Object.keys(errorlist[item])] =
                    errorlist[item][Object.keys(errorlist[item])];
                }
              }
              setErrors(outputErrors);
            } else {
              commonAction.setSubmitSnackbar({
                status: true,
                messages: error.response.data.error,
                error: true,
              });
            }
          });
      } else {
        await userAction
          .addUser({
            orgs_id: orgs,
            ...temp,
            inactivedate: inactivedate ? moment(inactivedate).format(globalDateEntity.dateTimeFormatPost) : null,
            user_policies,
            permissions: permissions,
          })
          .then(function (response) {
            commonAction.setSubmitSnackbar({
              status: true,
              messages: stringConstants.addedRecordMsg,
            });
            setPolicyListField(false);
            resetForm();
            setPolicyListFieldKey(new Date().getTime());
            setIsDirtyForm(false);
            onDone();
          })
          .catch(function (error) {
            if(error.response && error.response.status === 422 && error.response.data.error_type === "validation_error") {
              let outputErrors = {};
              const errorlist = error.response.data.error;
              for (let item in errorlist) {
                if(userInfo && 
                  userInfo.roles && 
                  userInfo.roles.title && 
                  (userInfo.roles.title.toLowerCase() !== currentUserEntity.platformAdminTitle.toLowerCase())
                ) {
                  if(errorlist && errorlist[item] && Object.keys(errorlist[item]) && Object.keys(errorlist[item])[0] === 'email') {
                    outputErrors['email_username'] =
                      errorlist[item][Object.keys(errorlist[item])];
                  } else {
                    outputErrors[Object.keys(errorlist[item])] =
                      errorlist[item][Object.keys(errorlist[item])];
                  }                  
                } else {
                  outputErrors[Object.keys(errorlist[item])] =
                    errorlist[item][Object.keys(errorlist[item])];
                }
              }
              setErrors(outputErrors);
            } else {
              commonAction.setSubmitSnackbar({
                status: true,
                messages: error.response.data.error,
                error: true,
              });
            }
          });
      }
    } catch (err) {
      console.log("err", err)
    }
  };

  function handleSwitch(event, setFieldValue) {
    if (event.target.checked) {
      setFieldValue('orgs', orgId);
    }
    else {
      setFieldValue('root_user', 0);
    }
    setIsTenantAdmin( event.target.checked ? true : false );
  }


  React.useEffect(() => {

    if (isTenantAdmin != initialTenantAdmin) {
      setIsDirtyForm(true);
    }
    else {
      setIsDirtyForm(false);
    }
  }, [isTenantAdmin, initialTenantAdmin])

  React.useEffect(() => {
    if (
      userInfo &&
      userInfo.roles &&
      userInfo.roles.title.toLowerCase() === currentUserEntity.platformAdminTitle
    ) {
      orgAction.getOrgsList();
    }

  }, [userInfo]);

  React.useEffect(() => {
    if (formValues && pageType!==globalPageTypeEntity.add) {
      //formValues.inactivedate = (formValues.inactivedate && formValues.current_status === 'INACTIVE') ? formValues.inactivedate : null;
      formValues.inactivedate = (formValues.inactivedate) ? formValues.inactivedate : null;
      //formValues.role = formValues.roles;
      formValues.orgs = formValues.orgs_id;

      // Need to change it to pull from permissions property

      if (!formValues.permissions) {
        formValues.userRoles = [
          {
            id: Math.random(), //NOSONAR
            productCode: "",
            roleId: ""
          }
        ]
      }
      else {

        let userRolesArray = [];

        Object.keys(formValues.permissions).forEach(function(item) {
          userRolesArray.push({
            id: Math.random(), //NOSONAR
            productCode: item,
            roleId: formValues.permissions[item]
          })
        });

        formValues.userRoles = userRolesArray;

      }
      //
      if (formValues.PK && formValues.userRoles && (formValues?.userRoles?.length>0)) {
        setIsTenantAdmin(false);
      }
      else {
        setIsTenantAdmin(true);
      }

      if (formValues.email && formValues.email.length > 0) {
        let emailDiassemble = formValues.email.split('@');
        formValues.email_username = emailDiassemble[0];
        formValues.email_domain = emailDiassemble[1];
      }
      formValues.showEmail = userInfo && userInfo.roles && userInfo.roles.title && userInfo.roles.title.toLowerCase() === currentUserEntity.platformAdminTitle.toLowerCase();

      if (formValues.userRoles && Array.isArray(formValues.userRoles) && formValues.userRoles.length>0) {
        formValues.userRoles.forEach(function(item) {
          
          if (item.productCode==='POLICIES') {
            formValues.role = item.roleId;
          }
        });
      }
      setFieldData(formValues);

      if (policyTypesData && policyTypesData.length>0 && formValues.user_policies && formValues.user_policies !=="" && formValues.user_policies.length>0) {

        let defaultPolicyTypes = policyTypesData.filter(({PK}) => {
          return formValues.user_policies.includes(PK);
        });

        setpolicyTypesDefaultValues(defaultPolicyTypes)
        setPolicyListField(true);
        setIsTenantAdmin(false);
      }
      else {
        setPolicyListField(false);
        setpolicyTypesDefaultValues([]);
      }

      if (formValues && (!formValues.permissions || formValues.permissions === null)) {
        setIsTenantAdmin(true);
        setInitialTenantAdmin(true);
      }
      else {
        setIsTenantAdmin(false);
        setInitialTenantAdmin(false);
      }

    }
  }, [formValues, configData, policyTypesData]);

  React.useEffect(() => {
    // settingsAction.getPolicyTypeList();
  }, [])

  React.useEffect(() => {
    setPolicyTypesData(settingsState.policyTypeList);
  }, [settingsState.policyTypeList]);

  React.useState(() => {
    if (userInfo && userInfo.roles && userInfo.roles.title && userInfo.roles.title.toLowerCase() !== currentUserEntity.platformAdminTitle.toLowerCase() ) {
      settingsAction.getPolicyTypeList();
    }
  }, []);

  return (
    <>
      <RouteLeavingGuard
        // When should shouldBlockNavigation be invoked,
        // simply passing a boolean
        // (same as "when" prop of Prompt of React-Router)
        when={isDirtyForm}
        // Navigate function
        navigate={(path) => history.push(path)}
        // Use as "message" prop of Prompt of React-Router
        shouldBlockNavigation={(location) => {
          // This case it blocks the navigation when:
          // 1. If user starting to type something in the form
          // 2. Then go to any other place confirmation popup should be prompt.
          // (Just an example, in real case you might
          // need to block all location regarding this case)
          return true;
        }}
      />
      <Formik
        initialValues={fieldData}
        onSubmit={onFormSubmit}
        validationSchema={schema}
        enableReinitialize={true}
        innerRef={formRef}
      >
        {(formikProps) => {
          const {
            values,
            touched,
            errors,
            dirty,
            setFieldValue,
            handleChange,
            handleBlur,
            handleSubmit,
          } = formikProps;
          return (

            <Box className={styles.dialogmainwrapper}>

              <form onSubmit={handleSubmit} noValidate>
                <Grid
                  container
                  spacing={2}
                  className={`addNewForm`}
                >
                  <Grid item md={8} xs={12}>
                    <Typography variant="h6" component="h6">{pageType == globalPageTypeEntity.add ? 'Add User' : (
                      (typeof(fieldData?.is_sso_user) !== 'undefined' && fieldData?.is_sso_user==='y') ? 'View User' : 'Edit User'
                    )}</Typography>
                  </Grid>
                  <Grid
                    item
                    md={12} xs={12}
                  >
                    <TextField
                      required
                      fullWidth
                      id="username"
                      name="username"
                      label="User Name"
                      data-testid="username"
                      variant="outlined"
                      value={values.username}
                      disabled={pageType!==globalPageTypeEntity.add || ( typeof(fieldData?.is_sso_user) !== 'undefined' && fieldData?.is_sso_user==='y') }
                      //onChange={handleChange}
                      onChange={(e, v) => {
                        setFieldValue('username', e.target.value, true);
                        setIsDirtyForm(true);
                      }}
                      onBlur={handleBlur}
                      error={errors.username && touched.username}
                      helperText={
                        errors.username &&
                        touched.username &&
                        errors.username
                      }
                    ></TextField>
                  </Grid>
                  <Grid
                    item
                    md={6} 
                    xs={6}
                  >
                    <TextField
                      required
                      fullWidth
                      id="firstname"
                      name="firstname"
                      label="First Name"
                      variant="outlined"
                      data-testid="firstname"
                      value={values.firstname}
                      disabled={typeof(fieldData?.is_sso_user) !== 'undefined' && fieldData?.is_sso_user==='y'}
                      //onChange={handleChange}
                      onChange={(e, v) => {
                        setFieldValue('firstname', e.target.value, true);
                        setIsDirtyForm(true);
                      }}
                      onBlur={handleBlur}
                      error={errors.firstname && touched.firstname}
                      helperText={
                        errors.firstname &&
                        touched.firstname &&
                        errors.firstname
                      }
                    ></TextField>
                  </Grid>
                  <Grid
                    item
                    md={6}
                    xs={6}
                  >
                    <TextField
                      required
                      fullWidth
                      id="lastname"
                      name="lastname"
                      label="Last Name"
                      data-testid="lastname"
                      variant="outlined"
                      value={values.lastname}
                      //onChange={handleChange}
                      disabled={typeof(fieldData?.is_sso_user) !== 'undefined' && fieldData?.is_sso_user==='y'}
                      onChange={(e, v) => {
                        setFieldValue('lastname', e.target.value, true);
                        setIsDirtyForm(true);
                      }}
                      onBlur={handleBlur}
                      error={errors.lastname && touched.lastname}
                      helperText={
                        errors.lastname &&
                        touched.lastname &&
                        errors.lastname
                      }
                    ></TextField>
                  </Grid>
                    {
                      (userInfo && userInfo.roles && userInfo.roles.title && (userInfo.roles.title.toLowerCase() !== currentUserEntity.platformAdminTitle.toLowerCase())) ? (<>
                        <Grid item xs={6}>
                            <input type="hidden" name="showEmail" id="showEmail" value={false} />
                            <TextField 
                                labelId="email_username-label"
                                id="email_username"
                                variant="outlined" 
                                placeholder="email" 
                                data-testid="email_username"
                                fullWidth
                                disabled={typeof(fieldData?.is_sso_user) !== 'undefined' && fieldData?.is_sso_user==='y'}
                                InputProps={{
                                  endAdornment: (
                                    "@"
                                  )
                                }}
                                required
                                name="email_username"
                                label="Email"
                                value={values.email_username}
                                //onChange={handleChange}
                                onChange={(e, v) => {
                                  
                                  setFieldValue('email_username', e.target.value, true);
                                  setIsDirtyForm(true);
                                }}
                                onBlur={handleBlur}
                                error={errors.email_username && touched.email_username}
                                helperText={
                                  errors.email_username &&
                                  touched.email_username &&
                                  errors.email_username
                                }
                              />
                        </Grid>
                        <Grid item xs={6}>     
                          <FormControl
                            variant="outlined"
                            fullWidth
                          >                            
                            <InputLabel id="email_domain-label">
                              Domain
                            </InputLabel>
                            <Select
                                labelId="email_domain-label"
                                id="email_domain"
                                placeholder="Domain"
                                name="email_domain"
                                data-testid="email_domain"
                                fullWidth
                                value={values.email_domain}
                                //onChange={handleChange}
                                disabled={typeof(fieldData?.is_sso_user) !== 'undefined' && fieldData?.is_sso_user==='y'}
                                onChange={(e, v) => {
                                  setFieldValue('email_domain', e.target.value, true);
                                  setIsDirtyForm(true);
                                }}
                                label="Domain"
                                variant="outlined"
                                className={`${classes.txtlowercase}`}
                                disableElevation
                                style={{'textDecoration': (domainsList.filter(i => (i.domain==values.email_domain && !i.active)).length > 0) ? 'line-through' : 'none' }}
                              >
                                {
                                  (domainsList && (domainsList.length>0)) ? domainsList.filter(i => {

                                    if (values.PK && values.PK.length > 0) { //edit
                                      return true;
                                    }
                                    else { //add
                                      return i.active;
                                    }
                                  }).map((option, index) => {
                                    if (option.domain) {
                                      return (
                                        <MenuItem key={`domain-${index}`} value={option.domain} style={{textDecoration: (!option.active) ? 'line-through' : 'none'  }} >
                                          {option.domain}
                                        </MenuItem>
                                      );
                                    }                                        
                                  })
                                  : ''}
                            </Select>  
                          </FormControl>                            
                        </Grid>
                      </>) : (
                        <Grid item xs={12}>
                          <input type="hidden" name="showEmail" id="showEmail" value={true} />
                          <TextField
                            required
                            fullWidth
                            id="email"
                            name="email"
                            label="Email"
                            data-testid="email"
                            variant="outlined"
                            disabled={typeof(fieldData?.is_sso_user) !== 'undefined' && fieldData?.is_sso_user==='y'}
                            value={values.email}
                            //onChange={handleChange}
                            onChange={(e, v) => {
                              setFieldValue('email', e.target.value, true);
                              setIsDirtyForm(true);
                            }}
                            onBlur={handleBlur}
                            error={errors.email && touched.email}
                            helperText={
                              errors.email && touched.email && errors.email
                            }
                          ></TextField>
                        </Grid>
                      )
                    }                  
                  <Grid
                    item
                    md={6}
                    xs={6}
                  >
                    <MuiPhoneNumber
                      countryCodeEditable={false}
                      id="phone"
                      name="phone"
                      label="Phone"
                      variant="outlined"
                      data-testid="phone"
                      value={values.phone}
                      fullWidth
                      disabled={typeof(fieldData?.is_sso_user) !== 'undefined' && fieldData?.is_sso_user==='y'}
                      defaultCountry={'us'}
                      onlyCountries={['us']}
                      //onChange={handleChange('phone')}
                      onChange={(e, v) => {
                        setFieldValue('phone', e, true);
                        setIsDirtyForm(true);
                      }}
                      error={errors.phone && touched.phone}
                      helperText={
                        errors.phone && touched.phone && errors.phone
                      }
                      onBlur={handleBlur}
                      className={
                        touched.phone && errors.phone ? 'has-error' : null
                      }
                      InputProps={{
                        endAdornment: (
                          <PopoverBtn
                            icon="help-outline"
                            description={
                              <p>
                                {' '}
                                Valid Phone Numbers:
                                <br />
                                +1 (234) 567-8900
                              </p>
                            }
                          ></PopoverBtn>
                        )
                      }}
                    />
                  </Grid>
                  
                  <Grid
                    item
                    md={6}
                    xs={6}
                  >
                    <FormControl
                      variant="outlined"
                      className={classes.formControl}
                    >
                      <InputLabel id="demo-simple-select-outlined-label">
                        Status
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-outlined-label"
                        id="demo-simple-select-outlined"
                        placeholder="Status"
                        name="current_status"
                        value={values.current_status}
                        //onChange={handleChange}
                        disabled={typeof(fieldData?.is_sso_user) !== 'undefined' && fieldData?.is_sso_user==='y'}
                        data-testid="current_status"
                        onChange={(e, v) => {
                          setFieldValue('current_status', e.target.value, true);
                          setIsDirtyForm(true);
                        }}
                        label="Status"
                      >
                        <MenuItem value={'ACTIVE'}>Active</MenuItem>
                        <MenuItem value={'INACTIVE'}>Inactive</MenuItem>
                      </Select>
                      <FormHelperText
                        className={
                          errors.current_status &&
                            touched.current_status &&
                            errors.current_status
                            ? 'Mui-error'
                            : ''
                        }
                      >
                        {errors.current_status &&
                          touched.current_status &&
                          errors.current_status}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Divider />
                  {userInfo && userInfo.roles && userInfo.roles.title && (userInfo.roles.title.toLowerCase() !== currentUserEntity.platformAdminTitle.toLowerCase()) && (
                    <>
                    <Grid
                      item
                      md={12}
                    >
                      <h3>Access Details</h3>
                    </Grid>

                    <Grid
                      item
                      md={12}
                      xs={12}
                    >
                      Is this User a Tenant Administrator &nbsp;&nbsp;
                      <input type="hidden" name="isTenantAdmin" id="isTenantAdmin" value={isTenantAdmin} />
                      <FormControlLabel
                        data-testid="isTenantAdmin"
                        disabled={typeof(fieldData?.is_sso_user) !== 'undefined' && fieldData?.is_sso_user==='y'}
                        control={
                          <Switch
                            checked={isTenantAdmin}
                            onChange={(e) => {
                              handleSwitch(e, setFieldValue);
                            }}

                            color="primary"
                          />
                        }
                        label={isTenantAdmin ? 'Yes' : 'No'}
                      />

                      <input type="hidden" name="role" id="role" value={values.role} />
                    </Grid>
                    { /*isTenantAdmin && ((formValues.root_user == 1) || (userInfo.root_user == 1)) && formValues.roles_title */}
                    {((isTenantAdmin && userInfo.root_user == 1 && formValues.current_status == "ACTIVE") && (formValues.roles != null || formValues.role != null)) && <Grid
                      item
                      md={12}
                      xs={12}
                    >
                      Is this User a Default Tenant Admin? &nbsp;&nbsp;
                      <input type="hidden" name="root_user" id="root_user" value={values.root_user} />
                      <FormControlLabel
                        data-testid="isDefaultTenantAdmin"
                        disabled={userInfo.root_user != 1 || (userInfo?.title?.roles && userInfo?.title?.roles?.title && (userInfo?.title?.roles?.title?.toLowerCase() != currentUserEntity.tenantAdminTitle.toLowerCase()))}
                        control={
                          <Switch
                            checked={(values.root_user == 1) ? true : false}
                            onChange={(e) => {
                             userAction.updateUserRole({current_user:formValues.PK, login_user: userInfo.PK}).then(res=>{                               
                               formValues.root_user = res.data.data.root_user;
                               loginAction.getUserInfo();
                               cancelClick();
                             });
                            }}

                            //  onChange={(event) => {
                            //   setFieldValue('root_user', event.target.checked ? 1 : 0);                            
                            // }}
                            color="primary"
                          />
                        }
                        label={values.root_user == 1 ? 'Yes' : 'No'}
                      />                    
                    </Grid>}

                    {!isTenantAdmin && (<FieldArray name="userRoles">
                      {({ push, remove }) => (
                        <Grid item md={12} xs={12}>

                          {values.userRoles && values?.userRoles?.length>0 && values?.userRoles?.map((p, index) => {

                            const productCode = `userRoles[${index}].productCode`;
                            const touchedProductCode = getIn(touched, productCode);
                            const errorProductCode = getIn(errors, productCode);

                            const roleId = `userRoles[${index}].roleId`;
                            const touchedRoleId = getIn(touched, roleId);
                            const errorRoleId = getIn(errors, roleId);


                            return (<Grid key={`item_${index}`} container spacing={2}>
                              <Grid item md={2} xs={2}></Grid>
                              <Grid item md={4} xs={4}>
                                <FormControl
                                  required
                                  variant="outlined"
                                  className={classes.formControl}
                                >
                                  <InputLabel id={`product-code-${index}`}>
                                    Product
                                  </InputLabel>
                                  <Select
                                    style={{textTransform: 'capitalize'}}
                                    labelId={`product-code-${index}`}
                                    placeholder="Product"
                                    label="Product"
                                    name={productCode}
                                    value={p.productCode}
                                    required
                                    helperText={
                                      touchedProductCode && errorProductCode
                                        ? errorProductCode
                                        : ""
                                    }
                                    error={Boolean(touchedProductCode && errorProductCode)}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                  >
                                    <MenuItem value="">
                                      <em>Select</em>
                                    </MenuItem>
                                    {
                                      productRoles && Object.keys(productRoles).sort(function (textA, textB) {
                                        return textA < textB ? -1 : textA > textB ? 1 : 0;
                                      }).map((objItem) => {
                                        let found = values.userRoles && values.userRoles?.some(obj => obj.productCode === objItem);
                                        return (
                                          <MenuItem disabled={found} key={`product-${objItem}`} value={objItem} style={{textTransform: 'capitalize'}}>
                                            {objItem === "POLICIES" ? 'Policy' : objItem === "AUTO" ? 'Auto Authorization' : objItem?.toLowerCase()}
                                          </MenuItem>
                                        )
                                      })
                                    }
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item md={4} xs={4}>
                                <FormControl
                                  required
                                  variant="outlined"
                                  data-testid="role-control"
                                  className={classes.formControl}
                                >
                                  <InputLabel id={`roleId-${index}`}>
                                    Role
                                  </InputLabel>
                                  <Select
                                    labelId={`roleId-${index}`}
                                    placeholder="Role"
                                    label="Role"
                                    name={roleId}
                                    value={p.roleId}
                                    required
                                    helperText={
                                      touchedRoleId && errorRoleId
                                        ? errorRoleId
                                        : ""
                                    }
                                    error={Boolean(touchedRoleId && errorRoleId)}
                                    //onChange={handleChange}
                                    onBlur={handleBlur}
                                    onChange={(e, value) => {
                                      if (p.productCode === 'POLICIES') {
                                        if (
                                          value.props.children ===
                                          'Policy Operator' || value.props.children === 'Policy Manager'
                                        ) {
                                          setPolicyListField(true);
                                        } else {
                                          setFieldValue('user_policies', []);
                                          setPolicyListField(false);
                                        }

                                        setFieldValue('role', e.target.value);
                                        
                                      }

                                      let foundPoliciesProduct = false;

                                      values.userRoles.forEach(function(item) {
                                        if (item.productCode === 'POLICIES') {
                                          foundPoliciesProduct = true;
                                        }
                                      });

                                      if (!foundPoliciesProduct) {
                                        setFieldValue('role', '');
                                      }

                                      setFieldValue(roleId, e.target.value);
                                      setIsDirtyForm(true);

                                      setFieldValue('orgs', orgId);

                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>Select</em>
                                    </MenuItem>

                                    {
                                      p.productCode && productRoles && productRoles[p.productCode] && productRoles[p.productCode].sort(function(a, b) {
                                        var textA = a.title.toUpperCase();
                                        var textB = b.title.toUpperCase();
                                        return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
                                    }).map((objItem) => {
                                        return (
                                          <MenuItem key={`product-${objItem.PK}`} value={objItem.PK}>{objItem.title}</MenuItem>
                                        )
                                      })
                                    }

                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item md={2} xs={2}>
                                {(values.userRoles.length==1 || index==values.userRoles.length-1) && (productRoles ? (values.userRoles.length < Object.keys(productRoles).length) : true) && (<Tooltip title="Add more"><IconButton
                                  onClick={() =>
                                    push({ id: Math.random(), productCode: "", roleId: "" })  //NOSONAR
                                  }
                                >
                                  <AddBoxIcon />
                                </IconButton></Tooltip>)}

                                { ( (index<values.userRoles.length-1) || (productRoles ? (values.userRoles.length == Object.keys(productRoles).length) : true) ) && (<Tooltip title="Delete"><IconButton
                                  onClick={() => {
                                    remove(index);
                                    
                                    values.userRoles.forEach(function(item, i) {
                                      if (i==index && item.productCode=='POLICIES') {
                                        setPolicyListField(false);
                                        setPolicyTypesData([]);
                                        setFieldValue('user_policies', [], true);
                                      }
                                    });


                                  }}
                                >
                                    <DeleteIcon />
                                  </IconButton></Tooltip>)}
                                </Grid>

                              </Grid>)
                              })}

                          </Grid>
                        )}
                      </FieldArray>)}
                    </>
                  )}

                  {!isTenantAdmin && (policyListField===true) && (
                    <Grid
                      item
                      md={12}
                    >
                      <EnhancedAutoComplete
                        disabled={
                          (userInfo &&
                            userInfo.roles &&
                            userInfo.roles.title.toLowerCase() === currentUserEntity.platformAdminTitle &&
                            pageType === globalPageTypeEntity.edit)
                        }
                        error={errors.user_policies? true: false}
                        id="user_policies"
                        multiple={true}
                        required
                        key={policyListFieldKey}
                        defaultValue={policyTypesDefaultValues}
                        options={policyTypesData}
                        getOptionLabel={(option) => {
                          return `${option.title}`;
                        }}
                        renderOptionFunction={(option) => {
                          let dataItem = `${option.title}`;
                          return (
                            <>
                              <Tooltip title={dataItem} style={{ width: '100%' }}>
                                <Typography variant="body2" noWrap={true}>
                                  {dataItem}
                                </Typography>
                              </Tooltip>
                            </>
                          )
                        }}
                        label="Policy Types"
                        handleOnChange={
                          (e, value) => {
                            let data = value.map(({PK}) => PK);
                            setFieldValue('user_policies', data, true);
                          }
                        }
                      ></EnhancedAutoComplete>
                        <FormHelperText
                          className={
                            errors.user_policies && touched.user_policies
                              ? 'Mui-error'
                              : ''
                          }
                        >
                        {errors.user_policies && touched.user_policies && errors.user_policies}
                      </FormHelperText>
                    </Grid>
                  )}

                  <Grid
                    item
                    md={6}
                  >
                    {values.current_status === 'INACTIVE' && values.inactivedate && (
                      <KeyboardDatePicker
                        disabled={true}
                        variant="inline"
                        inputVariant="outlined"
                        label="End Date"
                        format={globalDateEntity.datePickerFormat}
                        className={classes.autoComp}
                        autoOk={true}
                        id="inactivedate"
                        name="inactivedate"
                        invalidLabel="Select a valid date format"
                        value={values.inactivedate}
                        onChange={(v) => {
                          setFieldValue('inactivedate', v ? v : '', true);
                        }}
                        onBlur={handleBlur}
                        minDate={new Date(DatepickerStartDate)}
                        minDateMessage={stringConstants.minDate}
                        error={errors.inactivedate && touched.inactivedate}
                        helperText={
                          errors.inactivedate &&
                          touched.inactivedate &&
                          errors.inactivedate
                        }
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={0}
                >
                  
                    <Grid
                      item
                      md={6}
                      className={styles.headerRightPlusIcon}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={cancelClick}
                        data-testid="cancel-button"
                        className={classes.button}
                        
                      >
                        Cancel
                      </Button>
                    </Grid>
                    
                    <Grid item md={6} >
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        data-testid="submit-button"
                        disabled={(!dirty && !isDirtyForm) || (typeof(fieldData?.is_sso_user) !== 'undefined' && fieldData?.is_sso_user==='y')}
                      >
                        Save
                      </Button>
                    </Grid>
                    
                </Grid>

              </form>
            </Box>
          );
        }}
      </Formik>
    </>
  );
};

export default UserFormFields;
